import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import {
  Send,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  PauseCircle,
  PlayCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Users,
  MessageSquare,
  Mail,
  DollarSign,
  Loader2,
  Trash2
} from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  draft: { color: 'gray', icon: Clock, label: 'Draft' },
  scheduled: { color: 'blue', icon: Clock, label: 'Scheduled' },
  running: { color: 'yellow', icon: Loader2, label: 'Sending...', animate: true },
  paused: { color: 'orange', icon: PauseCircle, label: 'Paused' },
  completed: { color: 'green', icon: CheckCircle, label: 'Completed' },
  failed: { color: 'red', icon: XCircle, label: 'Failed' },
};

const statusColorClasses = {
  gray: 'bg-gray-100 text-gray-700',
  blue: 'bg-blue-100 text-blue-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  orange: 'bg-orange-100 text-orange-700',
  green: 'bg-green-100 text-green-700',
  red: 'bg-red-100 text-red-700',
};

export default function CampaignsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useTenant();
  const dashData = useDashboardData() || {};
  const { refreshWallet } = dashData;
  const preloaded = dashData.campaignsPage;
  const [campaigns, setCampaigns] = useState(preloaded?.campaigns || []);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(preloaded ? Math.ceil((preloaded.total || 0) / 20) : 1);
  const [total, setTotal] = useState(preloaded?.total || 0);
  const skipInitialFetch = useRef(!!preloaded);
  const [channelFilter, setChannelFilter] = useState('');
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  
  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  // Modals (Preview and Send only - Create moved to dedicated page)
  const [showPreviewModal, setShowPreviewModal] = useState(null);
  const [showSendModal, setShowSendModal] = useState(null);

  // Preview/Send state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20',
      });
      if (channelFilter) params.set('channel', channelFilter);

      const response = await fetch(`${BACKEND_URL}/api/campaigns?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch campaigns');

      const data = await response.json();
      setCampaigns(data.campaigns || []);
      setTotalPages(Math.ceil((data.total || 0) / 20));
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, page, channelFilter]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Auto-poll when any campaign is running
  const hasRunning = campaigns.some(c => c.status === 'running');
  const pollRef = useRef(null);

  useEffect(() => {
    if (hasRunning) {
      pollRef.current = setInterval(() => {
        fetchCampaigns();
        if (refreshWallet) refreshWallet();
      }, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasRunning, fetchCampaigns, refreshWallet]);

  const fetchPreview = async (campaignId) => {
    setPreviewLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch preview');

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      console.error('Preview error:', err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    setSendLoading(true);
    setSendResult(null);

    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ confirmed_recipient_count: preview?.total_recipients || 0 }),
      });

      // Clone immediately so we can always read the body even if something else consumed it
      const cloned = response.clone();
      let data;
      try {
        data = await response.json();
      } catch {
        // If .json() fails (non-JSON response or body already read), try the clone
        try {
          const text = await cloned.text();
          data = JSON.parse(text);
        } catch {
          const fallbackText = await cloned.text().catch(() => '');
          throw new Error(fallbackText || `Server error (${response.status})`);
        }
      }

      if (!response.ok) {
        // Handle insufficient SMS credits error
        if (response.status === 400 && data.detail?.error_code === 'INSUFFICIENT_SMS_BALANCE') {
          setSendResult({
            success: false,
            insufficientBalance: true,
            message: data.detail.message || 'You don\'t have enough SMS credits',
            smsNeeded: data.detail.sms_needed,
            smsAvailable: data.detail.sms_available,
            estimatedCostCents: data.detail.estimated_cost_cents
          });
          return;
        }
        const errorMessage = typeof data.detail === 'string'
          ? data.detail
          : data.detail?.message || data.message || 'Failed to send campaign';
        throw new Error(errorMessage);
      }

      setSendResult({ success: true, message: data.message });
      fetchCampaigns();
      if (refreshWallet) refreshWallet();
    } catch (err) {
      setSendResult({ success: false, message: err.message });
    } finally {
      setSendLoading(false);
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const text = await response.text();
        let err;
        try { err = JSON.parse(text); } catch { throw new Error(text || 'Failed to pause campaign'); }
        throw new Error(err.detail || 'Failed to pause campaign');
      }

      fetchCampaigns();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm('Cancel this scheduled campaign? It will be reverted to draft.')) return;
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const text = await response.text();
        let err;
        try { err = JSON.parse(text); } catch { throw new Error(text || 'Failed to cancel campaign'); }
        throw new Error(err.detail || 'Failed to cancel campaign');
      }

      fetchCampaigns();
    } catch (err) {
      setError(err.message);
    }
  };

  const openSendModal = async (campaign) => {
    setShowSendModal(campaign);
    setSendResult(null);
    await fetchPreview(campaign.id);
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete draft "${campaign.name}"? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/campaigns/${campaign.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete campaign');
      }
      setSuccessMessage(`"${campaign.name}" deleted`);
      fetchCampaigns();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-600 mt-1">{total} total campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={channelFilter}
            onChange={(e) => { setChannelFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white"
          >
            <option value="">All Channels</option>
            <option value="sms">SMS Only</option>
            <option value="email">Email Only</option>
          </select>
          <OnboardingTooltip id="campaigns_create" text="Start a new campaign to reach your customers">
            <button
              onClick={() => navigate('/dashboard/campaigns/new')}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </OnboardingTooltip>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Campaign List */}
      <div className="space-y-4">
        {loading ? (
          /* Skeleton Loading State */
          [1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-6 w-24 rounded-full" />
                  </div>
                  <Skeleton className="h-4 w-full max-w-md" />
                  <div className="flex gap-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
                <div className="flex gap-2">
                    <Skeleton className="h-10 w-20 rounded-lg" />
                    <Skeleton className="h-10 w-24 rounded-lg" />
                </div>
              </div>
            </div>
          ))
        ) : campaigns.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <Send className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No campaigns yet</h3>
            <p className="text-gray-500 mb-4">Create your first SMS campaign to start reaching your customers.</p>
            <button
              onClick={() => navigate('/dashboard/campaigns/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              <Plus className="w-4 h-4" />
              Create Campaign
            </button>
          </div>
        ) : (
          <>
            {campaigns.map((campaign) => {
              const config = statusConfig[campaign.status] || statusConfig.draft;
              const StatusIcon = config.icon;
              
              return (
                <div
                  key={campaign.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
                  data-testid={`campaign-${campaign.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Campaign Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3
                          className="text-lg font-semibold text-gray-900 truncate cursor-pointer hover:text-orange-600 transition-colors"
                          onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
                        >
                          {campaign.name}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusColorClasses[config.color]}`}>
                          <StatusIcon className={`w-3.5 h-3.5${config.animate ? ' animate-spin' : ''}`} />
                          {config.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          campaign.channel === 'email'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {campaign.channel === 'email' ? <Mail className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                          {campaign.channel === 'email' ? 'Email' : 'SMS'}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                        {campaign.message_template}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {campaign.total_recipients} recipients
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {campaign.sent_count}{campaign.status === 'running' && campaign.total_recipients ? `/${campaign.total_recipients}` : ''} sent
                        </span>
                        {campaign.status === 'scheduled' && campaign.scheduled_at && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <Clock className="w-4 h-4" />
                            Scheduled: {new Date(campaign.scheduled_at).toLocaleString()}
                          </span>
                        )}
                        {campaign.status === 'failed' && (
                          <span className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-4 h-4" />
                            {campaign.failed_count} failed
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {campaign.status === 'draft' && (
                        <>
                          <button
                            onClick={() => handleDeleteCampaign(campaign)}
                            className="px-3 py-2 text-sm font-medium text-red-600 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200"
                            title="Delete draft"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setShowPreviewModal(campaign);
                              fetchPreview(campaign.id);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => openSendModal(campaign)}
                            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
                            data-testid={`send-campaign-${campaign.id}`}
                          >
                            Send Now
                          </button>
                        </>
                      )}
                      {campaign.status === 'scheduled' && (
                        <>
                          <button
                            onClick={() => {
                              setShowPreviewModal(campaign);
                              fetchPreview(campaign.id);
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                          >
                            Preview
                          </button>
                          <button
                            onClick={() => handleCancelCampaign(campaign.id)}
                            className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => openSendModal(campaign)}
                            className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
                          >
                            Send Now
                          </button>
                        </>
                      )}
                      {campaign.status === 'running' && (
                        <button
                          onClick={() => handlePauseCampaign(campaign.id)}
                          className="px-4 py-2 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100"
                        >
                          Pause
                        </button>
                      )}
                      {campaign.status === 'paused' && (
                        <button
                          onClick={() => openSendModal(campaign)}
                          className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600"
                        >
                          Resume
                        </button>
                      )}
                      {(campaign.status === 'completed' || campaign.status === 'failed') && (
                        <button
                          onClick={() => navigate(`/dashboard/campaigns/${campaign.id}`)}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          View Details
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" data-testid="preview-modal">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Campaign Preview</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Campaign</h3>
                <p className="text-gray-900 font-medium">{showPreviewModal.name}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Message</h3>
                <div className="bg-gray-50 rounded-lg p-4 text-gray-800">
                  {showPreviewModal.message_template}
                </div>
              </div>

              {previewLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                </div>
              ) : preview && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-900">{preview.total_recipients}</div>
                    <div className="text-sm text-blue-600">Recipients</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-900">${(preview.estimated_cost_cents / 100).toFixed(2)}</div>
                    <div className="text-sm text-green-600">Estimated Cost</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-900">{preview.segments_per_message}</div>
                    <div className="text-sm text-purple-600">Segments/Message</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-orange-900">{preview.estimated_segments}</div>
                    <div className="text-sm text-orange-600">Total Segments</div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPreviewModal(null);
                    setPreview(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {showPreviewModal.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPreviewModal(null);
                      openSendModal(showPreviewModal);
                    }}
                    className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Send Campaign
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" data-testid="send-modal">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Confirm Send</h2>
            </div>
            <div className="p-6 space-y-4">
              {sendResult ? (
                sendResult.insufficientBalance ? (
                  /* Insufficient SMS Credits State */
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-red-800">Insufficient SMS Credits</h3>
                          <p className="text-sm text-red-600 mt-1">
                            {sendResult.message}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">SMS Required</span>
                        <span className="font-medium text-red-600">{sendResult.smsNeeded || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">SMS Available</span>
                        <span className="font-medium">{sendResult.smsAvailable || 0}</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                        <span className="text-gray-600">SMS Needed</span>
                        <span className="font-medium text-orange-600">{Math.max(0, (sendResult.smsNeeded || 0) - (sendResult.smsAvailable || 0))}</span>
                      </div>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <p className="text-sm text-orange-800 mb-3">
                        <strong>Add SMS credits</strong> to send this campaign. Your rate is locked in at purchase.
                      </p>
                      <a
                        href="/dashboard/wallet"
                        className="block w-full py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-center font-medium"
                      >
                        Add SMS Credits
                      </a>
                    </div>
                  </div>
                ) : (
                  /* Normal success/error state */
                  <div className={`rounded-lg p-4 ${sendResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                    <div className="flex items-center gap-3">
                      {sendResult.success ? (
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500" />
                      )}
                      <div>
                        <h3 className={`font-medium ${sendResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {sendResult.success ? 'Campaign Started!' : 'Failed to Send'}
                        </h3>
                        <p className={`text-sm ${sendResult.success ? 'text-green-600' : 'text-red-600'}`}>
                          {sendResult.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-yellow-800">Ready to send?</h3>
                        <p className="text-sm text-yellow-600 mt-1">
                          {showSendModal.channel === 'email'
                            ? 'This will send emails to all opted-in recipients. Email credits will be consumed from your prepaid email packs.'
                            : 'This will send SMS messages to all opted-in recipients. SMS credits will be consumed from your prepaid packs.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">Campaign</h3>
                    <p className="text-gray-900 font-medium">{showSendModal.name}</p>
                  </div>

                  {previewLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                    </div>
                  ) : preview && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Recipients</span>
                        <span className="font-medium">{preview.total_recipients}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">SMS required</span>
                        <span className="font-medium">{preview.estimated_segments}</span>
                      </div>
                      <div className="flex justify-between text-xs border-t border-gray-200 pt-2">
                        <span className="text-gray-500">Estimated cost based on your current SMS credits</span>
                        <span className="font-medium text-gray-700">${((preview.estimated_cost_cents || 0) / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSendModal(null);
                    setSendResult(null);
                    setPreview(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {sendResult ? 'Close' : 'Cancel'}
                </button>
                {!sendResult && (
                  <button
                    type="button"
                    onClick={() => handleSendCampaign(showSendModal.id)}
                    disabled={sendLoading || previewLoading}
                    className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-send-btn"
                  >
                    {sendLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Now
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
